import { boolean, integer, jsonb, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

// =============================================================================
// UNDERLAND EVENT SERVICE — Neon mirror (D1, Jason 2026-07-08 01:21 EDT: DUAL —
// Durable Object event log + Neon mirror from day one). The DO
// (apps/underland-events-worker/src/EventLogDO.ts) is the source of truth for
// ordering and the wake decision; this table is an async-replicated PROJECTION
// of the same log, written off the hot ingest path, never the reverse. If the
// two ever disagree, the DO wins — see docs/design/underland-events/
// ARCHITECTURE.md §2.3. Additive-only (house rule 11): CREATE TABLE IF NOT
// EXISTS via the Neon tool when this lands, never drizzle-kit migrate on the
// shared branch. This file is the mirror-in-packages/schema half of that rule;
// the actual CREATE TABLE SQL ships separately as a .sql file, never run by
// this lane (see BUILD-PLAN.md's Phase 0/1 gate).
//
// tenant is REQUIRED on every row (ARCHITECTURE.md §7 — multi-project from day
// one; career-optimizer is tenant #1, nothing here hardcodes it).
// =============================================================================

/** One mirrored event row. Mirrors the envelope shape in ARCHITECTURE.md §2.1
 *  exactly — tenant/type/source/id/occurred_at required, payload/actor/priority
 *  optional. `id` is the producer-generated ULID (the idempotency key, also the
 *  DO's own row key) — NOT this table's own uuid pk, which exists only because
 *  every table in this codebase gets one (house convention, `id()`). */
export const underlandEvents = pgTable('underland_events', {
  id: id(),
  /** The producer's own idempotency-key ULID (ARCHITECTURE.md §2.1's `id`
   *  field) — unique per event, distinct from the row's own uuid `id` above.
   *  Named eventId to avoid colliding with the pk. */
  eventId: varchar('event_id', { length: 64 }).notNull().unique(),
  tenant: varchar('tenant', { length: 120 }).notNull(),
  type: varchar('type', { length: 160 }).notNull(),
  source: varchar('source', { length: 160 }).notNull(),
  occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
  priority: varchar('priority', { length: 16 }).$type<'low' | 'normal' | 'high' | null>(),
  /** Opaque event-shape-specific data — never parsed for directives by any
   *  consumer (ARCHITECTURE.md §2.1's own law, same untrusted-input-is-data
   *  posture as decision-board-worker's answer/note fields). */
  payload: jsonb('payload'),
  actor: varchar('actor', { length: 320 }),
  /** The intelligence layer's verdict for this event (§4): store_only |
   *  batched | waking. Null until classified (should be near-instant on the
   *  hot path, but the mirror write is async so a brief null window is
   *  expected, never treated as an error). */
  verdict: varchar('verdict', { length: 16 }).$type<'store_only' | 'batched' | 'waking' | null>(),
  /** true when the verdict came from the Haiku fallback (Phase 1) rather than
   *  a deterministic rule (Phase 0) — lets /report (Phase 3) compute the
   *  zero-inference-happy-path ratio directly from this column. */
  classifiedByModel: boolean('classified_by_model').notNull().default(false),
  ...timestamps,
});

// =============================================================================
// UNDERLAND AI CALL LOG — every Haiku classify call, audited (house rule 10:
// never silence the cost meter). Same shape discipline as
// packages/schema/src/operations.ts's aiCallLog, kept as its OWN table (not a
// shared one) because this service is standalone cross-project infrastructure
// (ARCHITECTURE.md's own framing: "the same tier as decision-board-worker and
// logsink-worker... not the product domain") and must not couple to
// career-optimizer's ai_call_log table or its userId shape.
// =============================================================================

export const underlandAiCallLog = pgTable('underland_ai_call_log', {
  id: id(),
  tenant: varchar('tenant', { length: 120 }).notNull(),
  /** The underland_events.event_id this call classified. */
  eventId: varchar('event_id', { length: 64 }).notNull(),
  eventType: varchar('event_type', { length: 160 }).notNull(),
  modelId: varchar('model_id', { length: 64 }).notNull(),
  verdict: varchar('verdict', { length: 16 }).$type<'store_only' | 'batched' | 'waking'>().notNull(),
  /** The model's one-line reason (ARCHITECTURE.md §4 step 2's fixed schema) —
   *  free text but bounded, never a raw payload echo. */
  reason: text('reason'),
  inputTokens: integer('input_tokens'),
  outputTokens: integer('output_tokens'),
  cachedTokens: integer('cached_tokens'),
  cacheHit: boolean('cache_hit'),
  costMicroUsd: integer('cost_micro_usd'),
  durationMs: integer('duration_ms'),
  errorMessage: text('error_message'),
  ...timestamps,
});
